import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ActionButton, Flex, Grid, Text, View } from "@adobe/react-spectrum";
import {
  DraftIcon,
  TrashIcon,
  MyMessagesIcon,
  SentIcon,
  FolderIcon,
} from "../../../components/protected/Icons/IconsLib";
import { InboxFill, X } from "react-bootstrap-icons";
import moment from "moment";
import { useDependency } from "../../../contexts/DependencyProvider";
import { useTranslation } from "react-i18next";
import CommunicationList from "./component/CommunicationList";
import { useMessageCount } from "../../../contexts/MessageCountContext";
import { useViewInfo } from "../../../hooks/UseViewInfo";
import { useDeviceDetection } from "../../../hooks/UseDeviceDetection";
import { Loader } from "@progress/kendo-react-indicators";
import { GetNodeRequest } from "../../../services/soap/tree/reqeusts/GetNodeRequest";
import { ListNodeRequest } from "../../../services/soap/tree/reqeusts/ListNodeRequest";
import { ListMessageContainerNotificationRequest } from "../../../services/soap/message/requests/ListMessageContainerNotificationRequest";
import type { Filter } from "../../../services/soap/dataObjects/Filter";
import { subscribe, unsubscribe, AtollonEvent } from "../../../infra/events/Events";
import styles from "./communication_menu.module.css";

interface ICommunicationMenuProps {
  showMenu?: boolean;
  setShowMobileMenu?: (show: boolean) => void;
  setIsCommMenuOpen?: (isOpen: boolean) => void;
}

interface MessageFolderNode {
  id: number;
  name: string;
  childCount?: number;
  nodeType?: string;
  type?: string;
  module?: string;
  /** ListMessageContainerNotification (team inbox) */
  containerGroup?: string;
}

const CommunicationMenu: React.FC<ICommunicationMenuProps> = ({
  showMenu = false,
  setShowMobileMenu,
  setIsCommMenuOpen,
}) => {
  const { t } = useTranslation();
  const { store, treeService, messageService } = useDependency();
  const { isMobile } = useViewInfo();
  const deviceInfo = useDeviceDetection(isMobile);
  const shouldUseDesktopLayout =
    !deviceInfo.shouldUseMobileLayout() ||
    ((deviceInfo.isTablet || deviceInfo.isIPad) &&
      deviceInfo.orientation === "landscape");
  const isCompactLayout = !shouldUseDesktopLayout;
  const isTruePhoneLayout =
    isCompactLayout && !deviceInfo.isTablet && !deviceInfo.isIPad;
  const isTabletLandscapeDesktop =
    shouldUseDesktopLayout &&
    (deviceInfo.isTablet || deviceInfo.isIPad) &&
    deviceInfo.orientation === "landscape";
  const messageRootNode = store.session?.sessionInfo?.msgNode;
  const [show, setShow] = useState(false);
  const [selectedRecordView, setSelectedRecordView] = useState<
    "todo" | "messages" | "sent" | "trash" | "corporate" | "concept" | ""
  >("todo");
  const [customFolders, setCustomFolders] = useState<MessageFolderNode[]>([]);
  const [teamInboxFolders, setTeamInboxFolders] = useState<MessageFolderNode[]>([]);
  const [selectedCustomFolder, setSelectedCustomFolder] =
    useState<MessageFolderNode | null>(null);
  const [draggedMessage, setDraggedMessage] = useState<any | null>(null);
  const [dragTargetFolderId, setDragTargetFolderId] = useState<number | null>(
    null,
  );
  const moveMessageHandlerRef = useRef<
    ((messageId: string, newNode: string | number) => Promise<boolean>) | null
  >(null);
  const { totalUnreadMessage, totalUnreadTodo, isLoading } = useMessageCount();

  useEffect(() => {
    let isMounted = true;

    const loadCustomFolders = async () => {
      try {
        const parentNode = Number(messageRootNode || 0);
        if (!parentNode) {
          if (isMounted) {
            setCustomFolders([]);
          }
          return;
        }

        const rootNode = await treeService.getNode(
          new GetNodeRequest(store.Server, store.SessionId, {
            treeHandle: String(parentNode),
          }),
        );

        const folderParentNode = Number(rootNode?.NODE?.parent || 0) || parentNode;

        const response = await treeService.listNode(
          new ListNodeRequest(store.Server, store.SessionId, {
            parent: folderParentNode,
            limit: 200,
            offset: 0,
            orderBy: "name",
            countOnly: false,
            recursive: 0,
          }),
        );

        if (!isMounted) return;

        const nodes = Array.isArray(response?.NODE) ? response.NODE : [];
        setCustomFolders(
          nodes.filter(
            (node: MessageFolderNode) =>
              node?.id &&
              Number(node.id) !== parentNode &&
              node.module === "Messaging" &&
              node.type === "Message" &&
              typeof node.name === "string" &&
              node.name.trim().length > 0,
          ),
        );
      } catch (error) {
        console.error("Error loading communication folders:", error);
        if (isMounted) {
          setCustomFolders([]);
        }
      }
    };

    loadCustomFolders();

    return () => {
      isMounted = false;
    };
  }, [messageRootNode, store.Server, store.SessionId, treeService]);

  const loadTeamInboxFolders = useCallback(async () => {
    const userId = String(store.UserId || "").trim();
    if (!userId) {
      setTeamInboxFolders([]);
      return;
    }

    try {
      const FILTER: Filter = {
        COL: [{ name: "MessageRecipient", type: "equals", value: userId }],
      };
      const res: any = await messageService.listMessageContainerNotification(
        new ListMessageContainerNotificationRequest(store.Server, store.SessionId, {
          limit: "200",
          offset: "0",
          FILTER,
        }),
      );
      const rows = Array.isArray(res?.ROW) ? res.ROW : [];
      const items: MessageFolderNode[] = rows
        .filter((row: any) => String(row?.MessageContainerName ?? "") !== "My Messages")
        .map((row: any) => ({
          id: Number(row?.MessageContainer ?? 0) || 0,
          name: String(row?.MessageContainerName ?? "").trim(),
          containerGroup: String(row?.MessageContainerGroup ?? "").trim() || undefined,
        }))
        .filter((x: MessageFolderNode) => x.id && x.name);
      setTeamInboxFolders(items);
    } catch (e) {
      console.error("Error loading team inbox folders:", e);
      setTeamInboxFolders([]);
    }
  }, [messageService, store.Server, store.SessionId, store.UserId]);

  useEffect(() => {
    void loadTeamInboxFolders();
  }, [loadTeamInboxFolders]);

  useEffect(() => {
    const onUpdated = () => {
      void loadTeamInboxFolders();
    };
    subscribe(AtollonEvent.TEAM_INBOX_UPDATED, onUpdated);
    return () => unsubscribe(AtollonEvent.TEAM_INBOX_UPDATED, onUpdated);
  }, [loadTeamInboxFolders]);

  const activeViewLabel = useMemo(() => {
    if (!selectedCustomFolder) return undefined;
    const isTeam = teamInboxFolders.some((x) => x.id === selectedCustomFolder.id);
    return isTeam ? `Team Inbox: ${selectedCustomFolder.name}` : selectedCustomFolder.name;
  }, [selectedCustomFolder, teamInboxFolders]);

  const handleClose = () => {
    setShow(false);
    setShowMobileMenu?.(true);
  };

  const clearDragState = () => {
    setDraggedMessage(null);
    setDragTargetFolderId(null);
  };

  const handleDesktopRowDragStart = useCallback((message: any | null) => {
    setDraggedMessage(message);
    if (!message) {
      setDragTargetFolderId(null);
    }
  }, []);

  const handleRegisterMoveMessage = useCallback(
    (
      handler:
        | ((messageId: string, newNode: string | number) => Promise<boolean>)
        | null,
    ) => {
      moveMessageHandlerRef.current = handler;
    },
    [],
  );

  const updateFilterParams = (name: string, type: string, value: string) => {
    let ownerObj = {};
    let greaterObj = {};

    if (name === "state") {
      ({ value, ownerObj } = handleStateFilter(name, value, ownerObj));
    }

    if (name === "finalDate") {
      ({ value, greaterObj } = handleFinalDateFilter(
        name,
        type,
        value,
        greaterObj,
      ));
    }
  };

  const handleFilterType = (
    filterTypeName: any,
    filterValueStr: any,
    filterValue: any,
  ) => {
    switch (filterTypeName) {
      case "type":
        updateFilterParams(
          "projectType",
          "equals",
          filterValueStr === "All types" ? "" : filterValueStr,
        );
        break;
      case "state":
        updateFilterParams(
          "state",
          "in",
          filterValue === "All records" ? "" : filterValue,
        );
        break;
      case "period":
        updateFilterParams(
          "finalDate",
          filterValue,
          filterValue === "Any period" ? "" : filterValue,
        );
        break;
      case "customState":
        updateFilterParams(
          "customState",
          "in",
          filterValueStr === "All states" ? "" : filterValueStr,
        );
        break;
      case "user":
        updateFilterParams(
          "ownerId",
          "equals",
          filterValueStr === "All users" ? "" : filterValueStr,
        );
        break;
      default:
        break;
    }
  };

  const createParams = async (filterTypeName: any, filterValue: any) => {
    const filterValueStr =
      filterTypeName === "state"
        ? filterValue
        : Array.from(filterValue).join(",");
    handleFilterType(filterTypeName, filterValueStr, filterValue);
  };

  const handleStateFilter = (name: string, value: string, ownerObj: any) => {
    if (value === "todo") {
      value = "1,2";
      ownerObj = { name: "ownerId", type: "equals", value: store.UserId };
    }
    return { value, ownerObj };
  };

  const handleFinalDateFilter = (
    name: string,
    type: string,
    value: string,
    greaterObj: any,
  ) => {
    if (type === "less") {
      value = moment().format("YYYY-MM-DDTHH:MM:ss");
    } else if (type === "lessOrEquals") {
      value = moment().add(3, "months").format("YYYY-MM-DDT00:00:00");
      greaterObj = {
        name: "finalDate",
        type: "greaterOrEquals",
        value: moment().format("YYYY-MM-DDT00:00:00"),
      };
    } else if (type === "greater") {
      value = moment().format("YYYY-MM-DDT00:00:00");
    }
    return { value, greaterObj };
  };

  //create component of following structure
  const circleCounter = (totalCount: number) => (
    <Flex
      width={"28px"}
      direction={"column"}
      position={"static"}
      UNSAFE_className={styles.counter_circle}
      alignItems={"center"}
      justifyContent={"center"}
    >
      {totalCount}
    </Flex>
  );

  const NavItems = ({ onSelect }: { onSelect?: () => void }) => {
    const navItems = [
      {
        key: "todo" as const,
        label: t("Todo", { ns: "layout_components" }),
        icon: (
          <i
            className="bi bi-check-square"
            style={{ fontSize: "18px", paddingRight: "5px" }}
          />
        ),
        badge: isLoading ? (
          <Loader type="infinite-spinner" size="small" />
        ) : totalUnreadTodo > 0 ? (
          circleCounter(totalUnreadTodo)
        ) : null,
      },
      {
        key: "messages" as const,
        label: t("My Messages", { ns: "layout_components" }),
        icon: (
          <span style={{ paddingRight: "5px" }}>
            <MyMessagesIcon size={18} />
          </span>
        ),
        badge: isLoading ? (
          <Loader type="infinite-spinner" size="small" />
        ) : totalUnreadMessage > 0 ? (
          circleCounter(totalUnreadMessage)
        ) : null,
      },
      {
        key: "sent" as const,
        label: t("Sent", { ns: "layout_components" }),
        icon: (
          <span style={{ paddingRight: "5px" }}>
            <SentIcon size={18} />
          </span>
        ),
        badge: null,
      },
      {
        key: "concept" as const,
        label: t("Concept", { ns: "layout_components" }),
        icon: (
          <span style={{ paddingRight: "5px" }}>
            <DraftIcon size={18} />
          </span>
        ),
        badge: null,
      },
      {
        key: "trash" as const,
        label: t("Trash", { ns: "layout_components" }),
        icon: (
          <span style={{ paddingRight: "5px" }}>
            <TrashIcon size={18} />
          </span>
        ),
        badge: null,
      },
      {
        key: "corporate" as const,
        label: t("Corporate Messages", { ns: "layout_components" }),
        icon: (
          <span style={{ paddingRight: "5px" }}>
            <FolderIcon size={18} />
          </span>
        ),
        badge: null,
      },
    ];

    return (
      <Flex
        direction={"column"}
        width={"100%"}
        alignItems={"stretch"}
        justifyContent={"start"}
        gap={"12px"}
        UNSAFE_className={styles.views_menu_container}
      >
        <Flex
          direction={"column"}
          alignSelf={"stretch"}
          alignItems={"stretch"}
          justifyContent={"start"}
          gap={"2px"}
          UNSAFE_className={styles.message_views_parent}
        >
          {navItems.map((item) => (
            <button
              key={item.key}
              type="button"
              className={styles.message_view_button}
              onClick={() => {
                clearDragState();
                setSelectedCustomFolder(null);
                setSelectedRecordView(item.key);
                createParams("state", item.key);
                onSelect?.();
              }}
            >
              <Flex
                direction={"row"}
                alignItems={"center"}
                justifyContent={"space-between"}
                UNSAFE_className={
                  !selectedCustomFolder && selectedRecordView === item.key
                    ? styles.message_views_container_selected
                    : styles.message_views_container
                }
              >
                <Flex
                  direction={"row"}
                  flex={1}
                  alignItems={"center"}
                  justifyContent={"start"}
                  gap={"size-100"}
                  UNSAFE_style={{ minWidth: 0 }}
                >
                  <Flex
                    alignItems={"center"}
                    justifyContent={"center"}
                    UNSAFE_className={styles.message_views_icon_slot}
                    UNSAFE_style={{
                      color:
                        selectedRecordView === item.key ? "#1976d2" : "#8f9fa6",
                    }}
                  >
                    {item.icon}
                  </Flex>
                  <Flex
                    flex={1}
                    UNSAFE_className={styles.message_views_text}
                    UNSAFE_style={{ minWidth: 0 }}
                  >
                    <span>{item.label}</span>
                  </Flex>
                </Flex>
                <Flex
                  alignItems={"center"}
                  justifyContent={"end"}
                  UNSAFE_className={styles.message_views_badge_slot}
                >
                  {item.badge}
                </Flex>
              </Flex>
            </button>
          ))}

          {teamInboxFolders.length > 0 && (
            <View
              UNSAFE_style={{
                borderTop: "1px solid #e7edf0",
                marginTop: "8px",
                paddingTop: "8px",
              }}
            >
              <View
                UNSAFE_className={styles.views_menu_heading}
                UNSAFE_style={{ paddingBottom: "6px" }}
              >
                <Text UNSAFE_className={styles.menu_heading_text}>
                  {t("Team Inbox", {
                    ns: "layout_components",
                    defaultValue: "Team Inbox",
                  })}
                </Text>
              </View>
              {teamInboxFolders.map((folder) => (
                <button
                  key={`teamInbox-${folder.id}`}
                  type="button"
                  className={styles.message_view_button}
                  onDragOver={(event) => {
                    if (!shouldUseDesktopLayout || !draggedMessage) return;
                    event.preventDefault();
                    event.dataTransfer.dropEffect = "move";
                    setDragTargetFolderId(folder.id);
                  }}
                  onDragLeave={() => {
                    if (!shouldUseDesktopLayout) return;
                    setDragTargetFolderId((current) =>
                      current === folder.id ? null : current,
                    );
                  }}
                  onDrop={async (event) => {
                    if (
                      !shouldUseDesktopLayout ||
                      !draggedMessage ||
                      !moveMessageHandlerRef.current
                    ) {
                      return;
                    }

                    event.preventDefault();
                    event.stopPropagation();

                    const draggedMessageId = String(
                      draggedMessage.messageId ?? draggedMessage.id ?? "",
                    );
                    const followReference = String(
                      draggedMessage.followReference ?? "0",
                    ).trim();
                    const originalMessageId =
                      followReference && followReference !== "0"
                        ? followReference
                        : draggedMessageId;

                    if (!originalMessageId) {
                      clearDragState();
                      return;
                    }

                    await moveMessageHandlerRef.current(originalMessageId, folder.id);
                    clearDragState();
                  }}
                  onClick={() => {
                    clearDragState();
                    setSelectedCustomFolder(folder);
                    setSelectedRecordView("");
                    onSelect?.();
                  }}
                >
                  <Flex
                    direction={"row"}
                    alignItems={"center"}
                    justifyContent={"space-between"}
                    UNSAFE_className={
                      selectedCustomFolder?.id === folder.id
                        ? styles.message_views_container_selected
                        : styles.message_views_container
                    }
                    UNSAFE_style={
                      dragTargetFolderId === folder.id
                        ? {
                            backgroundColor: "#e8f4ff",
                            boxShadow: "inset 0 0 0 1px #1976d2",
                          }
                        : undefined
                    }
                  >
                    <Flex
                      direction={"row"}
                      flex={1}
                      alignItems={"center"}
                      justifyContent={"start"}
                      gap={"size-100"}
                      UNSAFE_style={{ minWidth: 0 }}
                    >
                      <Flex
                        alignItems={"center"}
                        justifyContent={"center"}
                        UNSAFE_className={styles.message_views_icon_slot}
                        UNSAFE_style={{
                          color:
                            selectedCustomFolder?.id === folder.id
                              ? "#1976d2"
                              : "#8f9fa6",
                        }}
                      >
                        <span style={{ paddingRight: "5px"}}>
                          <InboxFill size={18} fill={selectedCustomFolder?.id === folder.id ? "#1976d2" : "#8f9fa6"} />
                        </span>
                      </Flex>
                      <Flex
                        flex={1}
                        UNSAFE_className={styles.message_views_text}
                        UNSAFE_style={{ minWidth: 0 }}
                      >
                        <span>
                          {folder.name}
                          {/* <span className={styles.team_inbox_pill}>
                            {t("team_inbox_badge", {
                              ns: "layout_components",
                              defaultValue: "TEAM",
                            })}
                          </span> */}
                        </span>
                      </Flex>
                    </Flex>
                  </Flex>
                </button>
              ))}
            </View>
          )}

          {customFolders.length > 0 && (
            <View
              UNSAFE_style={{
                borderTop: "1px solid #e7edf0",
                marginTop: "8px",
                paddingTop: "8px",
              }}
            >
              {customFolders.map((folder) => (
                <button
                  key={folder.id}
                  type="button"
                  className={styles.message_view_button}
                  onDragOver={(event) => {
                    if (!shouldUseDesktopLayout || !draggedMessage) return;
                    event.preventDefault();
                    event.dataTransfer.dropEffect = "move";
                    setDragTargetFolderId(folder.id);
                  }}
                  onDragLeave={() => {
                    if (!shouldUseDesktopLayout) return;
                    setDragTargetFolderId((current) =>
                      current === folder.id ? null : current,
                    );
                  }}
                  onDrop={async (event) => {
                    if (
                      !shouldUseDesktopLayout ||
                      !draggedMessage ||
                      !moveMessageHandlerRef.current
                    ) {
                      return;
                    }

                    event.preventDefault();
                    event.stopPropagation();

                    const draggedMessageId = String(
                      draggedMessage.messageId ?? draggedMessage.id ?? "",
                    );
                    const followReference = String(
                      draggedMessage.followReference ?? "0",
                    ).trim();
                    const originalMessageId =
                      followReference && followReference !== "0"
                        ? followReference
                        : draggedMessageId;

                    if (!originalMessageId) {
                      clearDragState();
                      return;
                    }

                    await moveMessageHandlerRef.current(originalMessageId, folder.id);
                    clearDragState();
                  }}
                  onClick={() => {
                    clearDragState();
                    setSelectedCustomFolder(folder);
                    setSelectedRecordView("");
                    onSelect?.();
                  }}
                >
                  <Flex
                    direction={"row"}
                    alignItems={"center"}
                    justifyContent={"space-between"}
                    UNSAFE_className={
                      selectedCustomFolder?.id === folder.id
                        ? styles.message_views_container_selected
                        : styles.message_views_container
                    }
                    UNSAFE_style={
                      dragTargetFolderId === folder.id
                        ? {
                            backgroundColor: "#e8f4ff",
                            boxShadow: "inset 0 0 0 1px #1976d2",
                          }
                        : undefined
                    }
                  >
                    <Flex
                      direction={"row"}
                      flex={1}
                      alignItems={"center"}
                      justifyContent={"start"}
                      gap={"size-100"}
                      UNSAFE_style={{ minWidth: 0 }}
                    >
                      <Flex
                        alignItems={"center"}
                        justifyContent={"center"}
                        UNSAFE_className={styles.message_views_icon_slot}
                        UNSAFE_style={{
                          color:
                            selectedCustomFolder?.id === folder.id
                              ? "#1976d2"
                              : "#8f9fa6",
                        }}
                      >
                        <span style={{ paddingRight: "5px" }}>
                          <FolderIcon size={18} />
                        </span>
                      </Flex>
                      <Flex
                        flex={1}
                        UNSAFE_className={styles.message_views_text}
                        UNSAFE_style={{ minWidth: 0 }}
                      >
                        <span>{folder.name}</span>
                      </Flex>
                    </Flex>
                  </Flex>
                </button>
              ))}
            </View>
          )}
        </Flex>
      </Flex>
    );
  };

  if (shouldUseDesktopLayout && !isTabletLandscapeDesktop) {
    // ── DESKTOP: 2-column layout (left nav | message content) ──
    return (
      <div className={styles.desktopLayout}>
        <Grid
          areas={["nav content"]}
          columns={["275px", "auto"]}
          rows={["1fr"]}
          width="100%"
          UNSAFE_style={{ height: "100%" }}
        >
          {/* Left nav panel — always visible on desktop */}
          <View
            gridArea={"nav"}
            backgroundColor={"static-white"}
            UNSAFE_className={styles.desktopNavPanel}
          >
            <NavItems />
          </View>

          {/* Right: CommunicationList (Tabs + MessagesComponent with its own list|detail split) */}
          <View
            gridArea={"content"}
            UNSAFE_style={{ height: "100%", overflow: "hidden" }}
          >
            <CommunicationList
              setShow={setShow}
              setSelectedRecordView={setSelectedRecordView}
              setShowMobileMenu={setShowMobileMenu}
              messageType={selectedCustomFolder ? "messages" : selectedRecordView}
              customTreeHandle={selectedCustomFolder?.id}
              activeViewLabel={activeViewLabel}
              onDesktopRowDragStart={handleDesktopRowDragStart}
              onRegisterMoveMessage={handleRegisterMoveMessage}
            />
          </View>
        </Grid>
      </div>
    );
  }

  // ── MOBILE: custom views drawer + full-width content ──
  return (
    <>
      {isTabletLandscapeDesktop && show && (
        <div className={styles.mobile_drawer_overlay} onClick={handleClose}>
          <div
            className={styles.mobile_drawer_panel}
            onClick={(event) => event.stopPropagation()}
          >
            <Flex
              direction={"row"}
              alignItems={"center"}
              justifyContent={"space-between"}
              UNSAFE_className={styles.mobile_drawer_header}
            >
              <Text UNSAFE_className={styles.mobile_drawer_title}>
                {t("Views", {
                  ns: "layout_components",
                  defaultValue: "Views",
                })}
              </Text>
              <ActionButton
                isQuiet
                onPress={handleClose}
                aria-label={t("Close", {
                  ns: "layout_components",
                  defaultValue: "Close",
                })}
                UNSAFE_className={styles.mobile_drawer_close}
              >
                <X size={18} />
              </ActionButton>
            </Flex>
            <NavItems onSelect={handleClose} />
          </div>
        </div>
      )}

      {isCompactLayout && show && (
        <div className={styles.mobile_drawer_overlay} onClick={handleClose}>
          <div
            className={styles.mobile_drawer_panel}
            onClick={(event) => event.stopPropagation()}
          >
            <Flex
              direction={"row"}
              alignItems={"center"}
              justifyContent={"space-between"}
              UNSAFE_className={styles.mobile_drawer_header}
            >
              <Text UNSAFE_className={styles.mobile_drawer_title}>
                {t("Views", {
                  ns: "layout_components",
                  defaultValue: "Views",
                })}
              </Text>
              <ActionButton
                isQuiet
                onPress={handleClose}
                aria-label={t("Close", {
                  ns: "layout_components",
                  defaultValue: "Close",
                })}
                UNSAFE_className={styles.mobile_drawer_close}
              >
                <X size={18} />
              </ActionButton>
            </Flex>
            <NavItems onSelect={handleClose} />
          </div>
        </div>
      )}

      <View
        UNSAFE_className={
          isTruePhoneLayout ? styles.mobileAppScreen : undefined
        }
        UNSAFE_style={
          isTruePhoneLayout
            ? undefined
            : {
                height: "100%",
                width: "100%",
                backgroundColor: "#f5f5f5",
                overflow: "hidden",
              }
        }
      >
        <CommunicationList
          setShow={setShow}
          setSelectedRecordView={setSelectedRecordView}
          setShowMobileMenu={setShowMobileMenu}
          messageType={selectedCustomFolder ? "messages" : selectedRecordView}
          customTreeHandle={selectedCustomFolder?.id}
          activeViewLabel={activeViewLabel}
          onDesktopRowDragStart={handleDesktopRowDragStart}
          onRegisterMoveMessage={handleRegisterMoveMessage}
        />
      </View>
    </>
  );
};

export default CommunicationMenu;
